/**
 * Landlord University — `/university`.
 *
 * Curated lesson hub for new and growing Canadian landlords. Static
 * content for now (5 categories × 4-6 lessons each). Each lesson can
 * deep-link into existing /blog posts or external authoritative sources.
 *
 * SEO-optimized: H1, H2 per category, descriptive lesson titles + summaries.
 * No paywall — designed for organic discovery + as a backlink target.
 */
import { Link } from "react-router-dom";
import BackToPortalButton from "@/components/BackToPortalButton";
import {
  GraduationCap, ArrowRight, BookOpen, Wrench, Calculator, TrendingUp, Home,
  Lightbulb, Scale, Hammer, FileText, Building2, Sparkles
} from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";

const CATEGORIES = [
  {
    id: "start", icon: GraduationCap, color: "from-emerald-500 to-teal-500",
    title: "Getting started",
    blurb: "Buy your first rental, register the business, and avoid the rookie mistakes.",
    lessons: [
      { t: "First-time landlord checklist (Canada)",    s: "What you need before tenant move-in: lease, insurance, business number, GST/HST registration.", to: "/blog" },
      { t: "Choosing a property type that scales",       s: "House vs. condo vs. multiplex — operating-cost differences and cap-ex risk.", to: "/blog" },
      { t: "Registering as a landlord by province",      s: "Where you legally must register, and where you don't.", to: "/laws/on" },
      { t: "Picking the right rental insurance",         s: "Landlord, liability and rent-loss coverage explained in plain English.", to: "/blog" },
    ],
  },
  {
    id: "manage", icon: Home, color: "from-sky-500 to-indigo-500",
    title: "Day-to-day property management",
    blurb: "Tenant screening, leases, rent collection, communication — the boring work that compounds.",
    lessons: [
      { t: "How to screen Canadian tenants legally",    s: "Equifax credit pulls, income docs, references — without breaching PIPEDA.", to: "/blog" },
      { t: "Writing a lease tenants actually read",      s: "Province-specific clauses, the schedule-A trick, e-signatures that hold up.", to: "/blog" },
      { t: "Collecting rent (without chasing it)",       s: "Pre-authorized debit, late-fee policy that complies with each RTA.", to: "/blog" },
      { t: "Tenant communication scripts that work",     s: "Late-rent · noise complaint · entry notice — copy-paste templates.", to: "/blog" },
      { t: "How to fill out an N4 notice in Ontario",    s: "Avoid the 7 most common clerical mistakes that get an N4 tossed at the LTB.", to: "/blog/n4-notice-ontario-how-to-fill-out" },
    ],
  },
  {
    id: "diy", icon: Hammer, color: "from-amber-500 to-orange-500",
    title: "DIY maintenance & repairs",
    blurb: "The 80/20 of fix-it skills every landlord should own before calling a pro.",
    lessons: [
      { t: "5 plumbing fixes that save $400 each",       s: "Shut-off valves, P-trap, toilet flapper, water-hammer, vacuum-breaker.", to: "/blog" },
      { t: "Drywall repair end-to-end",                  s: "Patch a 2-inch hole to a 12-inch hole — sand, prime and blend texture.", to: "/blog" },
      { t: "Painting a unit in under 6 hours",           s: "Trim cut-ins, roller technique, picking a colour tenants don't argue with.", to: "/blog" },
      { t: "Annual furnace, A/C and dryer-vent service", s: "What to DIY vs. when to pay a tech with a manometer.", to: "/blog" },
      { t: "Winterizing in cold-climate Canada",         s: "Frozen-pipe prevention, outdoor faucet bleed, basement humidity.", to: "/blog" },
    ],
  },
  {
    id: "tax", icon: Calculator, color: "from-violet-500 to-purple-500",
    title: "Tax & compliance",
    blurb: "T776, CCA, NR4, GST/HST rebates — the CRA chapters that move the needle.",
    lessons: [
      { t: "T776 for Canadian landlords — complete 2026 guide", s: "Line-by-line: gross rent, vacancy losses, expense categories, CCA — with real examples.", to: "/blog/t776-form-canadian-landlords-guide" },
      { t: "When to claim CCA — and when NOT to",        s: "The recapture trap on disposition and how to time it.", to: "/blog" },
      { t: "Principal-residence exemption + s.45 elections", s: "Defer recapture for up to 4 years when you convert to a rental.", to: "/blog" },
      { t: "GST/HST New Residential Rental Rebate",      s: "Up to $30,300 federal rebate — and when it actually applies.", to: "/blog" },
      { t: "Alberta security deposit interest 2026",     s: "Annual interest rate, formula, and a copy-paste spreadsheet calculator.", to: "/blog/alberta-security-deposit-interest-calculator-2026" },
    ],
  },
  {
    id: "growth", icon: TrendingUp, color: "from-rose-500 to-pink-500",
    title: "Growing your portfolio",
    blurb: "Refinancing, BRRRR, partnerships, and when to swap doors for cash-flow.",
    lessons: [
      { t: "Underwriting a rental with DSCR (not cap rate)", s: "Why Canadian lenders care about debt-service coverage, and how to model it.", to: "/blog" },
      { t: "The Canadian BRRRR — done right",            s: "Buy · Rehab · Rent · Refinance · Repeat — Canadian-mortgage edition.", to: "/blog" },
      { t: "Setting up a holding company",               s: "When the corp tax savings beat the personal-deduction simplicity.", to: "/blog" },
      { t: "Partnership structures that don't blow up",   s: "Joint venture vs. partnership vs. equity — written agreements that survive.", to: "/blog" },
      { t: "Read the 2026 Canadian Landlord Report",      s: "Free 20+ page PDF: CMHC rents by province, CRA tax moves, 2026 outlook.", to: "/report/landlord-2026-canada" },
    ],
  },
];

const FOOTNOTE = (
  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mt-12 text-sm text-slate-700 leading-relaxed">
    <b className="text-slate-950">A note on these lessons.</b> Landlord University content is published for general education. Tax and legal references are kept current to the year noted on each lesson, but RTA rules, CRA forms, and lending requirements change frequently — always verify against the primary source (the linked RTA body, CRA form guide, or your lender) before acting.
  </div>
);

export default function LandlordUniversity() {
  usePageTitle(
    "Landlord University · Canadian landlord training, DIY tips & tax guides",
    "Free Canadian landlord training: tenant screening, lease writing, DIY repairs, T776 walkthroughs, BRRRR underwriting and more — by Central Rentals Canada."
  );

  return (
    <div className="bg-white text-slate-900 min-h-screen" data-testid="university-page">
      <div className="relative z-20 bg-white border-b border-slate-100"><PublicHeader light /></div>

      <main>
        <section className="relative overflow-hidden border-b border-slate-100">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-sky-50 pointer-events-none"/>
          <div className="relative max-w-5xl mx-auto px-6 md:px-8 pt-14 md:pt-20 pb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">
              <GraduationCap className="w-3 h-3"/> Landlord University · Free
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mt-5 text-slate-950">
              Become a better Canadian landlord —<br/>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 via-teal-500 to-sky-500">one lesson at a time.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-700 max-w-2xl leading-relaxed">
              {CATEGORIES.reduce((acc, c) => acc + c.lessons.length, 0)} curated lessons across {CATEGORIES.length} topic areas. Tenant screening, lease writing, DIY repairs, CRA tax, and how to grow your portfolio without losing your weekends. Paid plans.
            </p>
            <div className="mt-7 flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <a key={c.id} href={`#${c.id}`} data-testid={`uni-nav-${c.id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-emerald-300 hover:text-emerald-700 text-sm font-semibold text-slate-700 transition-colors">
                  {c.title}
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 md:px-8 py-12 md:py-16 space-y-14">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.id} id={c.id} className="scroll-mt-20" data-testid={`uni-cat-${c.id}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.color} text-white flex items-center justify-center flex-none shadow-lg`}>
                    <Icon className="w-6 h-6"/>
                  </div>
                  <div>
                    <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-950">{c.title}</h2>
                    <p className="mt-1.5 text-slate-600">{c.blurb}</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mt-7">
                  {c.lessons.map((l, i) => (
                    <Link key={i} to={l.to} data-testid={`uni-lesson-${c.id}-${i}`}
                      className="group bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all rounded-2xl p-5 flex flex-col">
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Lesson {String(i + 1).padStart(2, "0")}</div>
                      <h3 className="font-display text-base font-bold mt-1.5 text-slate-950 group-hover:text-emerald-700 transition-colors">{l.t}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed mt-2 flex-1">{l.s}</p>
                      <div className="text-sm font-semibold text-emerald-700 mt-3 inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                        Read lesson <ArrowRight className="w-3.5 h-3.5"/>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
          {FOOTNOTE}
        </section>

        <section className="bg-slate-950 text-white">
          <div className="max-w-5xl mx-auto px-6 md:px-8 py-14 md:py-16 text-center">
            <Sparkles className="w-8 h-8 mx-auto opacity-80"/>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold mt-3">Apply what you learn.</h2>
            <p className="mt-4 text-slate-300 max-w-2xl mx-auto">Central Rentals turns these lessons into automated workflows — compliant leases, screening, rent collection, T776 exports, all in one place.</p>
            <Link to="/signup?role=landlord" className="inline-flex items-center gap-2 mt-7 px-7 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold transition-colors">
              Choose a plan <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
        </section>
      </main>
      <BackToPortalButton />
    </div>
  );
}
